import React, { useState } from 'react';
import { createTestPanel } from '../../utils/TestPanelGenerator';

const API_URL = 'https://futr-bet-api.b-9f2.workers.dev';

// Test market for betting
const testMarket = {
  id: 'test-betting-market',
  question: 'Test Market for Betting Flow',
  description: 'This market is used to test the betting flow',
  slug: 'test-betting-flow',
  options: [
    { id: 'option-yes', text: 'YES', probability: 60, color: '#22c55e' },
    { id: 'option-no', text: 'NO', probability: 40, color: '#ef4444' }
  ],
  totalPool: 1000,
  endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  status: 'active'
};

// Test user for betting
const testUser = {
  id: 'test-user-betting',
  name: 'Test Bettor',
  balance: 1000,
  avatar: '🧪'
};

// Betting flow test cases
const bettingFlowTests = [
  {
    id: 'modal-open',
    name: '1. Bet Modal Opens',
    description: 'Testing if bet modal can be opened...',
    run: async () => {
      // In real implementation, this would check if modal component exists
      const modalExists = document.querySelector('.bet-modal') !== null;

      return {
        success: !modalExists, // Currently should fail since not implemented
        message: modalExists ? 'Bet modal found' : 'Bet modal not implemented yet',
        data: {
          expected: 'Modal component with market details',
          status: 'Not implemented'
        }
      };
    }
  },
  {
    id: 'option-select',
    name: '2. Option Selection Works',
    description: 'Testing if user can select betting option...',
    run: async () => {
      // Check if option selection is working
      const optionSelector = document.querySelector('.option-selector');

      return {
        success: false,
        message: 'Option selector not implemented',
        data: {
          expected: 'Clickable options with highlight on selection',
          required: ['YES option', 'NO option', 'Visual feedback']
        }
      };
    }
  },
  {
    id: 'amount-input',
    name: '3. Amount Input Validation',
    description: 'Testing bet amount input and validation...',
    run: async () => {
      // Test amount validation logic
      const testCases = [
        { amount: 100, valid: true, reason: 'Valid amount' },
        { amount: 0, valid: false, reason: 'Amount must be positive' },
        { amount: 1500, valid: false, reason: 'Exceeds balance' },
        { amount: -50, valid: false, reason: 'Negative not allowed' }
      ];

      return {
        success: false,
        message: 'Amount validation not implemented',
        data: {
          testCases,
          expected: 'Input with real-time validation'
        }
      };
    }
  },
  {
    id: 'balance-check',
    name: '4. Balance Validation',
    description: 'Testing if balance is checked before bet...',
    run: async () => {
      const userBalance = testUser.balance;
      const betAmount = 100;
      const canBet = betAmount <= userBalance;

      return {
        success: false,
        message: 'Balance validation UI not implemented',
        data: {
          userBalance,
          betAmount,
          canBet,
          expected: 'Disable bet button if insufficient balance'
        }
      };
    }
  },
  {
    id: 'probability-calc',
    name: '5. Probability Calculator',
    description: 'Testing probability impact calculation...',
    run: async () => {
      // CPMM calculation
      const poolYes = 600;
      const poolNo = 400;
      const k = poolYes * poolNo;
      const betAmount = 100;

      // Calculate shares out for YES bet
      const sharesOut = poolYes - (k / (poolNo + betAmount));
      const newPoolYes = poolYes - sharesOut;
      const newPoolNo = poolNo + betAmount;
      const newProbYes = (newPoolNo / (newPoolYes + newPoolNo)) * 100;

      return {
        success: false,
        message: 'Probability calculator UI not implemented',
        data: {
          currentProb: 60,
          betAmount: 100,
          newProb: newProbYes.toFixed(1),
          shares: sharesOut.toFixed(2),
          expected: 'Live calculation display as user types'
        }
      };
    }
  },
  {
    id: 'api-submission',
    name: '6. API Bet Submission',
    description: 'Testing bet submission to API...',
    run: async () => {
      try {
        // First create test user if needed
        const userResponse = await fetch(`${API_URL}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: `TestUser_${Date.now()}`,
            email: `test_${Date.now()}@futrbet.com`,
            balance: 1000
          })
        });
        const userData = await userResponse.json();
        const userId = userData.data?.id;

        // Create test market
        const marketResponse = await fetch(`${API_URL}/api/markets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question: `Test Betting Market ${Date.now()}`,
            description: 'Testing betting flow',
            options: [
              { text: 'YES', color: '#22c55e' },
              { text: 'NO', color: '#ef4444' }
            ],
            end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            creator_id: userId,
            category: 'test'
          })
        });
        const marketData = await marketResponse.json();
        const marketId = marketData.data?.id;

        if (!userId || !marketId) {
          return {
            success: false,
            message: 'Failed to create test data',
            data: { userId, marketId }
          };
        }

        // Try to place bet
        const betResponse = await fetch(`${API_URL}/api/bets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            market_id: marketId,
            option_id: 'test-option',
            amount: 100,
            shares: 100
          })
        });

        return {
          success: betResponse.ok,
          message: betResponse.ok ? 'API accepts bets' : 'API bet submission failed',
          data: await betResponse.json()
        };
      } catch (error) {
        return {
          success: false,
          message: `API error: ${error.message}`,
          data: { error }
        };
      }
    }
  },
  {
    id: 'balance-update',
    name: '7. Balance Updates After Bet',
    description: 'Testing if user balance updates correctly...',
    run: async () => {
      const initialBalance = 1000;
      const betAmount = 100;
      const expectedBalance = initialBalance - betAmount;

      return {
        success: false,
        message: 'Balance update UI not implemented',
        data: {
          initial: initialBalance,
          betAmount,
          expected: expectedBalance,
          uiUpdate: 'Should show immediate balance change'
        }
      };
    }
  },
  {
    id: 'market-update',
    name: '8. Market Probabilities Update',
    description: 'Testing if market updates after bet...',
    run: async () => {
      return {
        success: false,
        message: 'Market update not implemented',
        data: {
          expected: 'Probabilities should update in real-time',
          oldProb: { yes: 60, no: 40 },
          newProb: { yes: 67, no: 33 },
          visual: 'Smooth animation of probability bars'
        }
      };
    }
  },
  {
    id: 'bet-history',
    name: '9. Bet Appears in History',
    description: 'Testing if bet is saved to history...',
    run: async () => {
      const mockBet = {
        id: 'bet-123',
        marketId: testMarket.id,
        optionId: 'option-yes',
        amount: 100,
        shares: 83.33,
        timestamp: new Date().toISOString(),
        status: 'active'
      };

      return {
        success: false,
        message: 'Bet history not implemented',
        data: {
          expected: 'Bet should appear in portfolio/history',
          bet: mockBet,
          storage: 'Should persist in localStorage and DB'
        }
      };
    }
  },
  {
    id: 'error-handling',
    name: '10. Error Handling',
    description: 'Testing error cases and messages...',
    run: async () => {
      const errorScenarios = [
        { case: 'Insufficient balance', message: 'Not enough funds' },
        { case: 'Market closed', message: 'This market is closed' },
        { case: 'Network error', message: 'Connection failed. Retry?' },
        { case: 'Invalid amount', message: 'Enter a valid amount' }
      ];

      return {
        success: false,
        message: 'Error handling UI not implemented',
        data: {
          scenarios: errorScenarios,
          expected: 'Clear error messages with recovery options'
        }
      };
    }
  },
  {
    id: 'success-feedback',
    name: '11. Success Feedback',
    description: 'Testing success confirmation UI...',
    run: async () => {
      return {
        success: false,
        message: 'Success feedback not implemented',
        data: {
          expected: 'Success animation/message',
          features: [
            'Confirmation message',
            'New balance display',
            'Option to view in portfolio',
            'Option to place another bet'
          ]
        }
      };
    }
  },
  {
    id: 'mobile-ux',
    name: '12. Mobile Experience',
    description: 'Testing mobile-friendly betting...',
    run: async () => {
      const isMobile = window.innerWidth < 768;

      return {
        success: false,
        message: 'Mobile UX not optimized',
        data: {
          isMobile,
          expected: [
            'Full-screen modal on mobile',
            'Large touch targets (44px+)',
            'Number pad for amount',
            'Swipe gestures'
          ]
        }
      };
    }
  }
];

// Create the test panel
const BettingFlowTest = createTestPanel({
  title: '💰 Betting Flow Test Suite',
  description: 'Comprehensive tests for the complete betting experience',
  tests: bettingFlowTests
});

export default BettingFlowTest;